import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Mail, ArrowUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { label: t('nav.home'), href: '#home' },
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  const services = [
    t('services.items.engineering.title'),
    t('services.items.design.title'),
    t('services.items.modeling.title'),
    t('services.items.prototyping.title'),
  ];

  return (
    <footer className="relative bg-[#0F172A] border-t border-[#6B8CAE]/20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/1onyyd2p_ESOT%20Logo_.jpg" 
                alt="ESOT-EU Logo" 
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="text-[#94A3B8] max-w-md mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <motion.a
                href="mailto:info@esot-eu.com"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 border border-[#6B8CAE]/30 flex items-center justify-center hover:border-[#6B8CAE] hover:bg-[#6B8CAE]/10 transition-all"
                data-testid="footer-email-link"
              >
                <Mail size={18} className="text-[#6B8CAE]" />
              </motion.a>
              <motion.a
                href="https://www.linkedin.com/company/esot-eu-ltd/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 border border-[#6B8CAE]/30 flex items-center justify-center hover:border-[#6B8CAE] hover:bg-[#6B8CAE]/10 transition-all"
                data-testid="footer-linkedin-link"
              >
                <Linkedin size={18} className="text-[#6B8CAE]" />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">
              {t('footer.links')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                    className="text-[#94A3B8] hover:text-[#6B8CAE] transition-colors text-sm"
                    data-testid={`footer-link-${index}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">
              {t('footer.services')}
            </h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <span className="text-[#94A3B8] text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#6B8CAE]/10 flex flex-col md:flex-row items-center justify-between">
          <p className="text-[#94A3B8] text-sm">
            &copy; {currentYear} ESOT-EU. {t('footer.rights')}
          </p>
          
          {/* Scroll to Top */}
          <motion.button
            onClick={scrollToTop}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="mt-4 md:mt-0 w-10 h-10 border border-[#6B8CAE]/30 flex items-center justify-center hover:border-[#6B8CAE] hover:bg-[#6B8CAE]/10 transition-all"
            data-testid="scroll-to-top-button"
          >
            <ArrowUp size={18} className="text-[#6B8CAE]" />
          </motion.button>
        </div>
      </div>

      {/* Decorative Bottom Line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#6B8CAE] to-transparent"></div>
    </footer>
  );
};

export default Footer;
